import { FC } from 'react';
import classNames from 'classnames';
import { Container } from '@/components';
import { useIsMobile } from '@/hooks';

import style from './heroimage.module.scss';

interface HeroImageSkeletonProps {
  variation?: 'default' | 'heroImageWithHeadingAndDescrip';
  fullWidth?: boolean;
  withousVerticalSpace?: boolean;
  bottomSpacing?: boolean;
  backgroundColor?: string;
  imgEffect?: boolean;
  maxHeightDesktop?: number;
  maxHeightMobile?: number;
  borderRadius?: number;
}

const HeroImageSkeleton: FC<HeroImageSkeletonProps> = ({
  variation = 'default',
  fullWidth = true,
  withousVerticalSpace = true,
  bottomSpacing = false,
  backgroundColor = '',
  imgEffect = false,
  maxHeightDesktop = 0,
  maxHeightMobile = 0,
  borderRadius = 0,
}) => {
  const { isMobile } = useIsMobile();

  const isVariationWithHeading = variation === 'heroImageWithHeadingAndDescrip';

  const getMaxHeight = () => {
    const paddingTop = isMobile ? '58%' : '33.33%';

    if (isMobile && maxHeightMobile) {
      return `min(${maxHeightMobile}px, ${paddingTop})`;
    }

    if (!isMobile && maxHeightDesktop) {
      return `min(${maxHeightDesktop}px, ${paddingTop})`;
    }

    return undefined;
  };

  const renderSkeletonImage = (isMobile = false) => {
    return (
      <figure
        className={classNames(
          isMobile ? style.banner__figure_mobile : style.banner__figure,
          style.banner__figure_skeleton,
        )}
        style={{
          paddingTop: getMaxHeight(),
          borderRadius: borderRadius ? `${borderRadius}px` : 0,
        }}
      />
    );
  };

  const renderContentSkeleton = () => {
    if (!isVariationWithHeading) return null;

    return (
      <Container className={style.container__content}>
        <div className={style.content__description}>
          {/* Heading skeleton */}
          <div
            className={classNames(
              style.content__description__heading,
              style.skeleton__text,
            )}
          />
          {/* Title skeleton */}
          <div
            className={classNames(
              style.content__description__header,
              style.skeleton__text,
            )}
          />
          {/* Description skeleton */}
          <div
            className={classNames(
              style.content__description__descrip,
              style.skeleton__text,
            )}
          />
          {/* Button skeleton */}
          <div
            className={classNames(
              style.content__description__action,
              style.skeleton__button,
            )}
          />
        </div>
      </Container>
    );
  };

  return (
    <section
      className={classNames(style.hero, style.hero__skeleton, {
        [style.hero__spacing_bottom]: bottomSpacing,
      })}
      style={{ backgroundColor }}
    >
      <Container
        className={classNames(style.container__clear, {
          [style.container__full_width]: fullWidth,
          [style.container__withous_margin]: withousVerticalSpace,
          [style.container__with_margin]: !withousVerticalSpace,
          [style.container__effect_img]: imgEffect,
        })}
      >
        <div className={style.swipper__hero_image}>
          {renderContentSkeleton()}
          {renderSkeletonImage(false)} {/* Desktop image */}
          {renderSkeletonImage(true)} {/* Mobile image */}
        </div>
      </Container>
    </section>
  );
};

export default HeroImageSkeleton;
